"use client";

import * as React from "react";
import Link from "next/link";
import { Music2, Pause, Play } from "lucide-react";
import { usePlayerStore, type PlayerTrack } from "@gerama/stores";
import type { Song } from "@gerama/types";
import { cn } from "@gerama/utils";
import { MediaImage as Image } from "@/components/media-image";
import { artistPath } from "@/features/artists/artist-path";
import { publicPath } from "@/lib/public-path";

/** Square song card for grid views: cover with a play overlay, title and artists. */
function SongCardImpl({
  song,
  queue,
  contextLabel,
  contextHref,
}: {
  song: Song;
  queue: PlayerTrack[];
  contextLabel: string;
  contextHref?: string;
}) {
  const active = usePlayerStore((s) => s.current()?.kind === "song" && s.current()?.id === song.id);
  const playing = usePlayerStore((s) => s.isPlaying);

  const play = () => {
    const store = usePlayerStore.getState();
    if (active) return store.togglePlay();
    const index = queue.findIndex((t) => t.kind === "song" && t.id === song.id);
    store.playQueue(queue, Math.max(index, 0), { label: contextLabel, href: contextHref });
  };

  return (
    <div className="group min-w-0">
      <div className="bg-muted relative aspect-square overflow-hidden rounded-lg shadow-md shadow-black/30">
        {song.cover ? (
          <Image
            src={song.cover}
            alt=""
            fill
            sizes="(min-width: 1024px) 18vw, 45vw"
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <Music2 className="text-muted-foreground absolute inset-0 m-auto size-8" />
        )}
        <button
          type="button"
          onClick={play}
          aria-label={active && playing ? "توقف" : "پخش"}
          className={cn(
            "glass-primary text-primary-foreground absolute end-2 bottom-2 flex size-11 items-center justify-center rounded-full shadow-xl transition-all",
            active
              ? "opacity-100"
              : "translate-y-2 opacity-0 group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:translate-y-0 group-hover:opacity-100",
          )}
        >
          {active && playing ? (
            <Pause className="size-5 fill-current" />
          ) : (
            <Play className="size-5 fill-current" />
          )}
        </button>
      </div>
      <Link
        href={publicPath("songs", song)}
        className={cn(
          "mt-2 block truncate text-sm font-semibold hover:underline",
          active && "text-primary",
        )}
      >
        {song.name}
      </Link>
      <p className="text-muted-foreground truncate text-xs">
        {song.artists?.map((artist, i) => (
          <span key={artist.id}>
            {i > 0 && "، "}
            <Link href={artistPath(artist)} className="hover:text-foreground hover:underline">
              {artist.name}
            </Link>
          </span>
        ))}
      </p>
    </div>
  );
}

/** Memoised: a list re-renders when a page is appended, not every row with it. */
export const SongCard = React.memo(SongCardImpl);
